/* eslint-disable max-lines */
/* eslint-disable complexity */
/* eslint-disable indent */
import { ChevronSmallLeft } from '@elliemae/ds-icons';
import type { Theme } from '@elliemae/ds-system';
import { SimpleTruncatedTooltipText } from '@elliemae/ds-truncated-tooltip-text';
import React from 'react';
import { CHEVRON_BACK_DS_ID, LEFT_NAVIGATION_DATA_TESTID } from '../../constants/index.js';
import { ItemRenderer } from '../../exported-related/index.js';
import type { DSLeftNavigationT } from '../../react-desc-prop-types.js';
import {
  StyledBottomContainer,
  StyledChevronLeft,
  StyledItemLabel,
  StyledLeftContainer,
  StyledRightContainer,
} from '../styled.js';

const notOpenedShadowStyle = (isSelected: boolean) => (theme: Theme) =>
  `
  box-shadow: inset 4px 0 0 0 ${isSelected ? theme.colors.brand[400] : 'transparent'};

  :hover {
    box-shadow: inset 4px 0 0 0 ${isSelected ? theme.colors.brand[400] : theme.colors.brand[300]};
  }

  :active {
    box-shadow: inset 4px 0 0 0 ${theme.colors.brand[400]};
  }
`;

export const ItemSubmenu = (props: DSLeftNavigationT.ComponentProps): JSX.Element => {
  const {
    item,
    item: {
      dsId,
      itemOpts: { label, RightComponent, BottomComponent, labelDataTestId, labelOverflow: itemLabelOverflow },
      CollapsedComponent = () => null,
    },
    ctx,
    ctx: {
      leftNavProps,
      leftNavProps: { openedItem, labelOverflow: leftNavLabelOverflow, onItemClick },
      expandedForAnimation,
      selectedItem,
      selectedParent,
      setFocusedItem,
    },
  } = props;
  const getOwnerProps = React.useCallback(() => leftNavProps, [leftNavProps]);
  const getOwnerPropsArguments = React.useCallback(() => ({}), []);

  const labelOverflow = itemLabelOverflow || leftNavLabelOverflow;

  const opened = openedItem === dsId;

  const isSelected = selectedItem === dsId;

  const shadowStyle = React.useCallback(
    (opened_: boolean) => (theme: Theme) =>
      !opened_
        ? notOpenedShadowStyle(isSelected || selectedParent === dsId)(theme)
        : `box-shadow: inset 4px 0 0 0 #52A6EC`,
    [dsId, isSelected, selectedParent],
  );

  const ariaLabel = item.itemOpts.ariaLabel ?? (typeof label === 'string' ? `${label}, category` : 'category');

  return (
    <ItemRenderer
      item={{
        ...item,
        itemOpts: {
          ...item.itemOpts,
          ariaLabel,
          ariaExpanded: opened,
          openable: expandedForAnimation && !opened,
          closable: expandedForAnimation && opened,
          selectable: !opened,
        },
      }}
      shadowStyle={shadowStyle}
      hasBorderBottom={opened}
      borderBottomMr="12px"
    >
      <StyledLeftContainer
        cols={opened ? ['auto', 'auto'] : ['38px']}
        gutter="1px"
        pl={opened ? 'xxs' : 0}
        justifyItems="center"
        data-testid={LEFT_NAVIGATION_DATA_TESTID.LEFT_CONTAINER} // https://jira.elliemae.io/browse/PUI-15589
        getOwnerProps={getOwnerProps}
        getOwnerPropsArguments={getOwnerPropsArguments}
      >
        {opened && (
          <StyledChevronLeft
            buttonType="raw"
            onClick={(e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
              if (onItemClick) onItemClick(item, e as React.MouseEvent<HTMLElement>);
            }}
            tabIndex={0}
            onFocus={(e: React.FocusEvent) => {
              e.stopPropagation();
              setFocusedItem(CHEVRON_BACK_DS_ID);
            }}
            aria-label={`Close ${ariaLabel}`}
            data-testid={LEFT_NAVIGATION_DATA_TESTID.ITEM_CHEVRON_BACK} // https://jira.elliemae.io/browse/PUI-15589
            getOwnerProps={getOwnerProps}
            getOwnerPropsArguments={getOwnerPropsArguments}
          >
            <ChevronSmallLeft size="m" color={['brand-primary', '800']} />
          </StyledChevronLeft>
        )}
        {CollapsedComponent && <CollapsedComponent item={item} ctx={ctx} />}
      </StyledLeftContainer>
      <StyledItemLabel
        ml={opened ? 'xxs' : '1px'}
        mt="xxxs"
        mb="15px"
        labelOverflow={labelOverflow}
        data-testid={labelDataTestId || LEFT_NAVIGATION_DATA_TESTID.ITEM_LABEL}
        getOwnerProps={getOwnerProps}
        getOwnerPropsArguments={getOwnerPropsArguments}
      >
        {labelOverflow === 'truncate' ? <SimpleTruncatedTooltipText value={label} placement="bottom-start" /> : label}
      </StyledItemLabel>
      <StyledRightContainer
        pl="xs"
        pr={opened ? 'xxs2' : 'xxs'}
        onFocus={(e: React.FocusEvent) => {
          e.stopPropagation();
          setFocusedItem(null);
        }}
        onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}
        onClick={(e: React.MouseEvent) => openedItem === dsId && e.stopPropagation()}
        data-testid={LEFT_NAVIGATION_DATA_TESTID.RIGHT_CONTAINER} // https://jira.elliemae.io/browse/PUI-15589
        getOwnerProps={getOwnerProps}
        getOwnerPropsArguments={getOwnerPropsArguments}
      >
        {RightComponent && <RightComponent ctx={ctx} item={item} />}
      </StyledRightContainer>
      {BottomComponent && (
        <StyledBottomContainer
          style={{ gridColumn: 'span 3' }}
          pb="xxxs"
          pl="xxs"
          pr="xxs"
          onFocus={(e: React.FocusEvent) => {
            e.stopPropagation();
            setFocusedItem(null);
          }}
          onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}
          onClick={(e: React.MouseEvent) => openedItem && e.stopPropagation()}
          data-testid={LEFT_NAVIGATION_DATA_TESTID.BOTTOM_CONTAINER} // https://jira.elliemae.io/browse/PUI-15589
          getOwnerProps={getOwnerProps}
          getOwnerPropsArguments={getOwnerPropsArguments}
        >
          <BottomComponent ctx={ctx} item={item} />
        </StyledBottomContainer>
      )}
    </ItemRenderer>
  );
};
